import * as moment from 'moment';

/**
 * Derives the ICD-10 coding-rule context from a patient.
 *
 * Shared rather than inlined at each of the eight `app-icd-code-selector` call sites, because
 * the date parsing is the kind of thing that is written correctly once and slightly differently
 * the other seven times.
 *
 * ⚠ `patient.dob` IS 'DD/MM/YYYY'. `formatPatientDates` rewrites it on the way in, so
 * `new Date(patient.dob)` either fails outright or reads 05/06/1970 as 6 May. An age computed
 * from that is wrong by months and would fire the age rules against the wrong patients — which
 * teaches a clinician to ignore them.
 *
 * Both fields are optional in the result. A rule with no context stays SILENT rather than
 * guessing; a false alarm reads as the system not knowing the patient, and costs trust in every
 * other finding on the panel.
 */
export interface IcdCodingContext {
  sex: string | null;
  age: number | null;
}

/**
 * Memoised, because these are read from template getters.
 *
 * Eight `app-icd-code-selector` call sites each ask for sex and age on every change-detection
 * cycle, and a moment parse per cycle per selector is real work for an answer that changes once
 * a day. Keyed on the dob AND today's date, so it stays correct across midnight in a session
 * left open overnight rather than serving yesterday's age.
 */
const ageCache = new Map<string, number | null>();

/**
 * Parses only the two formats this application actually produces, in strict mode.
 *
 * ⚠ NO PERMISSIVE FALLBACK. `moment('01/31/1996')` succeeds by reading it as US MM/DD/YYYY, so a
 * permissive fallback silently accepts a format nothing here emits — and would read an ambiguous
 * '05/06/1970' one way here and the other way elsewhere. Refusing is the honest answer: an
 * unparseable date of birth means the age rules stay quiet, which is the safe direction.
 *
 * A `Date` or moment is taken as-is; only strings are format-checked.
 */
function parseKnownDate(value: unknown): moment.Moment {
  if (value instanceof Date) return moment(value);
  if (moment.isMoment(value)) return value as moment.Moment;

  const text = String(value).trim();
  // 'DD/MM/YYYY' — what `formatPatientDates` writes onto the patient object.
  const local = moment.utc(text, 'DD/MM/YYYY', true);
  if (local.isValid()) return local;
  // ISO — what an API returns, with or without a time part.
  return moment.utc(text, [moment.ISO_8601, 'YYYY-MM-DD'], true);
}

/** Whole years between the patient's date of birth and today, or null if it cannot be known. */
export function ageFromDob(dob: unknown): number | null {
  if (!dob) return null;

  const key = `${String(dob)}|${moment().format('YYYY-MM-DD')}`;
  if (ageCache.has(key)) return ageCache.get(key) as number | null;

  const parsed = parseKnownDate(dob);

  let years: number | null = null;
  if (parsed.isValid()) {
    const diff = moment.utc().diff(parsed, 'years');
    // A negative age means a typo in the date of birth, not a patient. Better no age than one
    // that quietly disables every age rule by being nonsense.
    years = Number.isFinite(diff) && diff >= 0 && diff <= 130 ? diff : null;
  }

  // Bounded so a long list view cannot grow it without limit.
  if (ageCache.size > 500) ageCache.clear();
  ageCache.set(key, years);
  return years;
}

/** 'M' | 'F' from whatever the record holds ('Male', 'female', 'F', …), or null. */
export function sexFromPatient(patient: any): string | null {
  const raw = String(patient?.gender ?? patient?.sex ?? '').trim().toUpperCase();
  if (raw.startsWith('M')) return 'M';
  if (raw.startsWith('F')) return 'F';
  return null;
}

export function icdCodingContext(patient: any): IcdCodingContext {
  return {
    sex: sexFromPatient(patient),
    age: ageFromDob(patient?.dob),
  };
}

/**
 * Normalises a date for `defaultOnsetDate` — the date a newly added code is dated from.
 *
 * ⚠ RETURNS 'YYYY-MM-DD' READ OFF THE LOCAL CALENDAR. The call sites hold dates in several
 * shapes: a moment, a `Date` from a picker, 'DD/MM/YYYY' from the patient object, an ISO string
 * from an API. `toISOString().slice(0, 10)` on a local midnight in SAST yields YESTERDAY, so a
 * code captured against an admission on the 14th would be dated the 13th.
 *
 * Mirrors `utils/icdDateUtils.js` server-side, which normalises again defensively.
 */
/**
 * Today, as a local calendar day.
 *
 * ⚠ NOT `new Date().toISOString().slice(0, 10)`. Before 02:00 SAST that returns YESTERDAY, so a
 * code added at 06:30 on the 14th would be dated the 13th.
 *
 * `now` is injectable ONLY so the check can pin that. Testing it against the current clock is
 * vacuous for most of the day — local and UTC agree from 02:00 to midnight in SAST — so the
 * assertion passed against a deliberately broken implementation when it was written at 08:00.
 * The check now supplies an instant just after local midnight, where the two always disagree.
 */
export function todayIsoDay(now: Date = new Date()): string {
  return moment(now).format('YYYY-MM-DD');
}

export function toOnsetDate(value: unknown): string | null {
  if (!value) return null;

  // Already a calendar day — returned untouched, with no Date round-trip for a zone to bite.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();

  // A Date or moment is a local instant; `.format` reads the LOCAL day, which is the point.
  if (value instanceof Date) return moment(value).format('YYYY-MM-DD');
  if (moment.isMoment(value)) return (value as moment.Moment).format('YYYY-MM-DD');

  const parsed = parseKnownDate(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}
